import React, { useEffect, useMemo } from "react";
import { ConnectionProvider, WalletProvider } from "@solana/wallet-adapter-react";
import { WalletModalProvider, useWalletModal } from "@solana/wallet-adapter-react-ui";
import { clusterApiUrl } from "@solana/web3.js";
import {
  // Phantom + Solflare auto-register as Standard Wallets on devices
  // where they're installed, but on mobile devices that DON'T yet have
  // them they wouldn't show in the picker — meaning users couldn't tap
  // to install or deep-link in. Including the explicit adapters keeps
  // them visible so those users have a clear path forward. Cosmetic
  // dup warning on installed devices is the worst-case trade-off.
  // The full list below is everything @solana/wallet-adapter-wallets
  // ships that's still actively maintained — so users with any of these
  // wallets, on desktop OR mobile, see their wallet in the picker.
  // Modern wallets like Backpack and Glow auto-register via the Wallet
  // Standard protocol so they appear automatically without an explicit
  // adapter; the list below covers the ones that don't.
  PhantomWalletAdapter,
  SolflareWalletAdapter,
  LedgerWalletAdapter,
  TrezorWalletAdapter,
  CoinbaseWalletAdapter,
  TrustWalletAdapter,
  BitgetWalletAdapter,
  Coin98WalletAdapter,
  MathWalletAdapter,
  TokenPocketWalletAdapter,
  NightlyWalletAdapter,
  HuobiWalletAdapter,
  BitpieWalletAdapter,
  XDEFIWalletAdapter,
  SafePalWalletAdapter,
  SalmonWalletAdapter,
  KrystalWalletAdapter,
  NufiWalletAdapter,
  CloverWalletAdapter,
  SpotWalletAdapter,
  NekoWalletAdapter,
  OntoWalletAdapter,
} from "@solana/wallet-adapter-wallets";

// Required by the default wallet-adapter-react-ui styling.
import "@solana/wallet-adapter-react-ui/styles.css";

// Bridges the legacy `appKitInstance.open()` callers — they dispatch
// `open-solana-wallet` and we trigger the Solana wallet modal here.
function ModalEventBridge() {
  const { setVisible } = useWalletModal();
  useEffect(() => {
    const handler = () => setVisible(true);
    window.addEventListener("open-solana-wallet", handler);
    return () => window.removeEventListener("open-solana-wallet", handler);
  }, [setVisible]);
  return null;
}

export default function SolanaWalletProvider({ children }) {
  const endpoint = useMemo(
    () => process.env.REACT_APP_SOLANA_RPC_URL || clusterApiUrl("mainnet-beta"),
    []
  );

  const wallets = useMemo(
    () => [
      // Tier 1 — most users will pick from here on desktop or mobile
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter(),
      new BitgetWalletAdapter(),
      new CoinbaseWalletAdapter(),
      new TrustWalletAdapter(),
      // Hardware wallets
      new LedgerWalletAdapter(),
      new TrezorWalletAdapter(),
      new SafePalWalletAdapter(),
      // Long tail — multi-chain, regional, niche but real user bases
      new XDEFIWalletAdapter(),
      new SalmonWalletAdapter(),
      new NightlyWalletAdapter(),
      new KrystalWalletAdapter(),
      new NufiWalletAdapter(),
      new TokenPocketWalletAdapter(),
      new MathWalletAdapter(),
      new Coin98WalletAdapter(),
      new CloverWalletAdapter(),
      new HuobiWalletAdapter(),
      new BitpieWalletAdapter(),
      new SpotWalletAdapter(),
      new NekoWalletAdapter(),
      new OntoWalletAdapter(),
    ],
    []
  );

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={wallets} autoConnect>
        <WalletModalProvider>
          <ModalEventBridge />
          {children}
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
}
